import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import type { OrderRow } from './orders-api.ts'
import { fmtStopAt, stopAddr, toStops } from './order/stops.ts'

/**
 * ══ Ô LẤY HÀNG / GIAO HÀNG TRÊN BẢNG ĐƠN — chép `StopCell` + `PhoneLink` (`Az.tsx:1026,1046`) ══
 *
 * Một ô gồm: giờ KẾ HOẠCH (đậm, kèm nhãn "KH") · SĐT bấm gọi + tên liên hệ · địa chỉ · "+N điểm
 * nữa" · nút 📁 mở hồ sơ điểm (giờ thực tế + chứng từ). Bấm vào THÂN Ô mở chi tiết đơn (việc của
 * `<td>` cha); chỉ nút 📁 mở `StopModal`.
 *
 * ⚠ Giờ THỰC TẾ chỉ còn ở tooltip của 📁 (bản cũ user 26/08): ô lớn luôn hiện giờ KH để số không
 *   nhảy giữa kế hoạch/thực tế khi điều hành vừa ghi xong.
 * ⚠ ICON LÀ SVG, KHÔNG PHẢI EMOJI 📁 — và đó là chỗ bản cũ đã mắc một lần (`Az.tsx:2`): emoji
 *   folder LUÔN vàng trên mọi hệ vì phông emoji màu phớt lờ CSS `color`, nên đổi trạng thái sang
 *   xám bằng `className` không ăn thua gì ("cái folder nó không màu xám" — user 27/08).
 * ⚠ KHÔNG hiện số đếm cạnh icon (user 27/08 "bỏ số 1 2, không cần đếm") — chỉ CÓ/KHÔNG bằng màu,
 *   số lượng xem trong hộp hồ sơ khi bấm vào.
 */

/** 📞 SĐT liên hệ (bản cũ user 26/08): máy tính bấm là COPY, mobile (pointer:coarse) bấm là GỌI. */
export const PhoneLink = ({ phone }: { phone: string }): JSX.Element => {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const isTouch = typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches
  return (
    <a href={isTouch ? `tel:${phone}` : undefined}
       onClick={(e) => {
         e.stopPropagation()
         if (isTouch) return
         e.preventDefault()
         void navigator.clipboard?.writeText(phone)
         setCopied(true)
         setTimeout(() => { setCopied(false) }, 1200)
       }}
       title={isTouch ? undefined : (copied ? t('sales.table.copiedTitle') : t('sales.table.copyPhoneTitle', { phone }))}
       className="text-brand-600 text-[11px] font-medium whitespace-nowrap hover:underline cursor-pointer">
      📞 {phone}{!isTouch && copied ? ' ✓' : ''}
    </a>
  )
}

export const StopCell = ({ order, kind, docSlots, onOpen }: {
  order: OrderRow
  kind: 'pickup' | 'drop'
  /**
   * Ngăn chứng từ ĐÃ CÓ TỆP của đơn (`OpsBoardRow.docSlots`) — icon 📁 tô CAM khi ngăn của điểm
   * đang hiện nằm trong đây. Chưa tải xong bảng điều hành ⇒ bỏ trống, icon xám như khi chưa có tệp
   * (KHÔNG đoán là có: tô cam rồi lại xám đi là màn tự đổi ý trước mắt người dùng).
   */
  docSlots?: readonly string[]
  /** Mở hồ sơ điểm (`StopModal`) — nút 📁. Bỏ trống ⇒ KHÔNG có nút (đơn hệ cũ: không có hồ sơ điểm trên rs-erp). */
  onOpen?: (() => void) | undefined
}): JSX.Element => {
  const { t } = useTranslation()
  const stops = toStops((kind === 'pickup' ? order.pickups : order.drops) ?? [])
  // Điểm CHÍNH: lấy = điểm đầu, giao = điểm cuối (bản cũ `primaryStop`, `Az.tsx:990`).
  const s = kind === 'pickup' ? stops[0] : stops[stops.length - 1]
  const actual = s?.actualAt
  /* Không có giờ riêng của điểm → lùi về NGÀY KẾ HOẠCH của đơn (bản cũ user 30/07): cột luôn có
     ngày để nhìn, khỏi trống '—'. */
  const planned = s?.at ?? (order.planDate !== '' ? `${order.planDate}T08:00` : '')
  const addr = stopAddr(s) || (kind === 'pickup' ? order.pickupAddr : order.dropAddr)
  const docsTitle = actual !== undefined
    ? `${t('azOps.stop.docsTooltip')} · ${t('azOps.stop.actual')}: ${fmtStopAt(actual)}`
    : t('azOps.stop.docsTooltip')
  /* Ngăn của các điểm — cùng cách đặt tên `StopDocs` dùng lúc tải lên (`pod:<id điểm>`). Điểm chưa
     có id ở máy chủ thì không ngăn nào mang tên nó ⇒ xám, đúng thực tế: chưa Lưu thì chưa neo được
     tệp vào đâu (xem chú thích đầu `StopModal`).
     ⚠ Xét MỌI điểm, không riêng điểm đang hiện: 📁 mở hộp chứa TẤT CẢ điểm, nên điểm nào có chứng từ
       thì icon phải vàng. Bản cũ chỉ xét điểm chính (`docN` của `primaryStop`) ⇒ đơn 2 điểm giao đính
       vào #1 mà ô hiện #2 vẫn xám (anh báo 21/10, RS-20261017-0053). */
  const hasDocs = stops.some((st) => (docSlots ?? []).includes(`pod:${st.id}`))
  return (
    <div className="w-full space-y-0.5 text-left">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="font-bold text-slate-700 text-[11px] whitespace-nowrap">{planned !== '' ? fmtStopAt(planned) : '—'}</span>
        {/* Nhãn LUÔN là "KH" khớp đúng số đang hiện (bản cũ user 26/08) — giờ thực tế chỉ ở tooltip 📁. */}
        {planned !== '' && <span className="text-[9px] font-bold text-slate-500 bg-slate-100 rounded px-1">{t('azOps.stop.plan')}</span>}
      </div>
      {/* Tên người liên hệ đi sau SĐT (bản cũ user 27/08) — chỉ hiện khi có nhập. */}
      {s?.phone !== undefined && (
        <div className="flex items-center gap-1 flex-wrap">
          <PhoneLink phone={s.phone} />
          {(s.contact ?? '') !== '' && <span className="text-slate-500 text-[11px] whitespace-nowrap">· {s.contact}</span>}
        </div>
      )}
      {addr !== '' && <div className="text-slate-500 text-[11px] leading-tight">{addr}</div>}
      {stops.length > 1 && <div className="text-[9px] text-slate-400 italic">{t('azOps.stop.morePoints', { n: stops.length - 1 })}</div>}
      {/* 📁 hồ sơ điểm — CĂN PHẢI đáy ô (bản cũ user 26/08). CAM khi điểm ĐÃ CÓ chứng từ, XÁM khi
          chưa. Xám là `slate-400` chứ KHÔNG phải `slate-300`: bản cũ đã thử `slate-300` và user báo
          quá nhạt, không phân biệt được hai trạng thái. */}
      {onOpen !== undefined && <div className="flex justify-end">
        <button type="button" onClick={(e) => { e.stopPropagation(); onOpen() }}
                aria-label={`${t(kind === 'pickup' ? 'azOps.stop.titlePickup' : 'azOps.stop.titleDrop')} ${order.ref}`}
                title={docsTitle}
                data-has-docs={hasDocs ? '1' : '0'}
                className={`shrink-0 inline-flex items-center gap-0.5 rounded px-0.5 transition ${hasDocs
                  ? 'text-amber-500 hover:text-amber-700 hover:bg-amber-50'
                  : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M10 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2z" />
          </svg>
        </button>
      </div>}
    </div>
  )
}
